import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Tabs,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { DEL_RETAILER } from "apollo/mutuations";
import { GET_RETAILERS } from "apollo/queries";
import RetailerAddModal from "components/Retailer/RetailerAddModal";
import RetailerImport from "components/Retailer/RetailerImport";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";

import RetailerDetailModal from "components/Retailer/RetailerDetail";
import RetailerEditModal from "components/Retailer/RetailerEditModal";

import ActionButton from "components/controls/ActionButtonNew";
import { Download, Trash, Upload } from "tabler-icons-react";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";
import RetailersWithSuppliers from "./retailersWithSuppliers";

const Retailers = () => {
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [openedImport, setOpenedImport] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  //pagination states
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState("10");
  const [activeTab, setActiveTab] = useState("all");

  const { data, loading, refetch } = useQuery(GET_RETAILERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      setOpenedDelete(false);
      setDeleteID(null);
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get retailers ",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.retailers.paginatorInfo.lastPage);
    }
  }, [data, size]);

  const [delRetailer] = useMutation(DEL_RETAILER, {
    update(cache, { data: { deleteRetailer } }) {
      cache.updateQuery(
        {
          query: GET_RETAILERS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: "",
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
          },
        },
        (data) => {
          if (data.retailers.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              retailers: {
                data: [
                  ...data.retailers.data.filter(
                    (retailer) => retailer.id !== deleteRetailer.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const deleteRetailer = () => {
    delRetailer({
      variables: { id: deleteID },

      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Retailer Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to delete retailer",
        });
      },
    });
  };

  const handleEditRetailer = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };
  useEffect(() => {
    refetch();
  }, []);
  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };
  const theme = useMantineTheme();
  const handleManageRetailer = (id) => {
    setEditId(id);
    setOpenedDetail(true);
  };
  const handleGeoLocationClick = (lat, lng) => {
    if (lat && lng) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };
  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      sortable: true,
      searchable: true,
      width: "25%",
      render: (rowData) => {
        return <span>{rowData.user?.name}</span>;
      },
    },
    {
      label: "City",
      key: "city",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.city}</span>;
      },
    },
    {
      label: "Phone",
      key: "contact_phone",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user?.phone}</span>;
      },
    },
    {
      label: "Register By",
      key: "Phone",
      sortable: false,
      width: "15%",
      exportable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.registeredBy?.name}</span>;
      },
    },
    {
      label: "Region",
      key: "city",
      searchable: true,
      exportable: false,
      width: "8%",
      render: (rowData) => {
        return <span>{rowData.region?.name}</span>;
      },
    },
    {
      label: "Balance",
      key: "balance",
      width: "5%",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return (
          <span>
            {rowData.wallet ? formatNumber(rowData.wallet.balance) : 0}
          </span>
        );
      },
    },
    {
      label: "Date",
      key: "created_at",
      exportable: false,
      sortable: true,
      searchable: false,
      render: (rowData) => <span>{rowData.created_at.split(" ")[0]}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      exportable: false,
      sortable: false,
      searchable: false,
      width: "22%",
      render: (rowData) => {
        const hasGeo = rowData?._geo && rowData?._geo.lat && rowData?._geo.lng;
        const lat = rowData?._geo?.lat;
        const lng = rowData?._geo?.lng;
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            {/* {hasGeo && (
              <ActionButton
                label="Edit"
                onClick={() => handleEditRetailer(`${rowData.id}`)}
              >
                <EditIcon style={{ fontSize: "1rem" }} />
              </ActionButton>
            )}
               */}
            <ActionButton
              color="success"
              label="View"
              onClick={() => handleManageRetailer(`${rowData.id}`)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>

            {hasGeo && (
              <ActionButton
                label="Location"
                color="primary"
                onClick={() => handleGeoLocationClick(lat, lng)}
              >
                <LocationOnIcon size={15} />
              </ActionButton>
            )}
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Retailer",
    setAddModal: setOpened,
  };
  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };
  const cityIndex = headerData.findIndex((h) => h.label === "City");
  const exportHeaderData = [
    ...headerData.slice(0, cityIndex + 1),
    {
      label: "Address",
      key: "address",
      render: (rowData) => {
        return rowData.address || "N/A";
      },
    },
    ...headerData.slice(cityIndex + 1, -1),
    {
      label: "Order Count",
      key: "orderCount",
      render: (rowData) => {
        return (
          <span>
            {rowData.orderCount ? formatNumber(rowData.orderCount) : 0}
          </span>
        );
      },
    },

    {
      label: "Location",
      render: (rowData) => {
        const lat = rowData?._geo?.lat;
        const lng = rowData?._geo?.lng;
        if (lat && lng) {
          return `[${lat},${lng}]`;
        }
        return "N/A";
      },
    },
  ];
  const [fetchAllRetailers] = useLazyQuery(GET_RETAILERS);

  const handleExportDataFetch = async () => {
    const retailers = [];

    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const { data } = await fetchAllRetailers({
        variables: {
          first: 1000,
          page,
          search: searchValue,
          ordered_by: [
            {
              column: "CREATED_AT",
              order: "DESC",
            },
          ],
        },
        fetchPolicy: "network-only",
      });

      const pageRetailers = data?.retailers?.data || [];
      const paginatorInfo = data?.retailers?.paginatorInfo;

      retailers.push(...pageRetailers);

      hasMorePages = paginatorInfo?.hasMorePages ?? false;

      page++;
    }

    return retailers;
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/retailers.xls";
    link.download = "retailers_template.xls";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <Tabs
      color="#FF6A00"
      value={activeTab}
      onTabChange={setActiveTab}
      styles={{
        tabList: {
          borderBottom: `2px solid #FF6A00`,
        },
        tab: {
          "&[data-active]": {
            borderColor: "#FF6A00",
          },
          "&:hover": {
            borderColor: "#FF6A00",
          },
        },
      }}
    >
      <Tabs.List>
        <Tabs.Tab value="all">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            All Retailers
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="withSuppliers">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Retailers With Suppliers
          </span>
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="all">
        <div style={{ width: "98%", margin: "auto" }}>
          <Drawer
            opened={openedEdit}
            overlayColor={
              theme.colorScheme === "dark"
                ? theme.colors.dark[9]
                : theme.colors.gray[2]
            }
            overlayOpacity={0.55}
            overlayBlur={3}
            title="Editing a Retailer"
            padding="xl"
            onClose={() => setOpenedEdit(false)}
            position="bottom"
            size="80%"
          >
            <RetailerEditModal setOpenedEdit={setOpenedEdit} editId={editId} />
          </Drawer>

          <Drawer
            opened={openedDetail}
            overlayColor={
              theme.colorScheme === "dark"
                ? theme.colors.dark[9]
                : theme.colors.gray[2]
            }
            overlayOpacity={0.55}
            overlayBlur={3}
            title="Retailer Detail"
            padding="xl"
            onClose={() => setOpenedDetail(false)}
            position="bottom"
            size="80%"
          >
            <RetailerDetailModal
              total={total}
              setTotal={setTotal}
              activePage={activePage}
              setActivePage={setActivePage}
              setOpenedDetail={setOpenedDetail}
              Id={editId}
            />
          </Drawer>

          <Modal
            opened={openedDelete}
            onClose={() => setOpenedDelete(false)}
            title="Warning"
            centered
          >
            <p>Are you sure do you want todelete this retailer?</p>
            <Group position="right">
              <Button onClick={() => deleteRetailer()} color="red">
                Delete
              </Button>
            </Group>
          </Modal>
          <Drawer
            opened={opened}
            onClose={() => setOpened(false)}
            title="Adding a Retailer"
            padding="xl"
            size="80%"
            position="bottom"
          >
            <RetailerAddModal
              total={total}
              setTotal={setTotal}
              activePage={activePage}
              setActivePage={setActivePage}
              setOpened={setOpened}
              size={size}
            />
          </Drawer>

          <Card shadow="sm" p="lg">
            <ScrollArea>
              <B2bTable
                total={total}
                paginatorInfo={data?.retailers?.paginatorInfo}
                activePage={activePage}
                handleChange={handleChange}
                clearInput={clearInput}
                handelSearch={handleManualSearch}
                searchValue={confirmedSearch}
                filterData={({ onCardClick }) => (
                  <Group
                    spacing="xs"
                    style={{ display: "flex", flexWrap: "nowrap" }}
                  >
                    <Button
                      style={{
                        backgroundColor: "#FF6A00",
                        color: "#FFFFFF",
                      }}
                      onClick={handleDownloadTemplate}
                      leftIcon={<Download size={16} />}
                    >
                      Download
                    </Button>
                    <Button
                      style={{
                        backgroundColor: "#FF6A00",
                        color: "#FFFFFF",
                      }}
                      onClick={() => setOpenedImport(true)}
                      leftIcon={<Upload size={16} />}
                    >
                      Import
                    </Button>
                    <ExportComponent
                      data={data ? data.retailers.data : []}
                      headers={exportHeaderData}
                      onFetch={handleExportDataFetch}
                      reportTitle="Retailers Report"
                      fileNamePrefix="retailers_report"
                    />
                  </Group>
                )}
                onSearchChange={setConfirmedSearch}
                header={headerData}
                optionsData={optionsData}
                loading={loading}
                data={data ? data.retailers.data : []}
                size={size}
                handlePageSizeChange={handlePageSizeChange}
              />
            </ScrollArea>
          </Card>
          <RetailerImport
            opened={openedImport}
            onClose={() => setOpenedImport(false)}
            onSuccess={() => {
              refetch();
            }}
          />
          <RetailerImport
            opened={openedImport}
            onClose={() => setOpenedImport(false)}
            onSuccess={() => {
              refetch();
            }}
          />
        </div>
      </Tabs.Panel>
      <Tabs.Panel value="withSuppliers">
        <div style={{ width: "98%", margin: "auto" }}>
          <RetailersWithSuppliers />
        </div>
      </Tabs.Panel>
    </Tabs>
  );
};

export default Retailers;
